/**
 * SeatManagement Component
 * 좌석 관리 UI (탭 구조)
 */
import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  CheckIcon,
  PaintBrushIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PlusCircleIcon,
  TrashIcon,
  HomeModernIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useSeatManagement } from '../use-cases/useSeatManagement';
import { seatManagementService } from '../services/SeatManagementService';
import type { ManagedSeat } from '../models/SeatManagement';
import { API_BASE_URL } from '../../../config/api';
import { apiClient } from '../../../infrastructure/api';

// 런타임 API URL 결정 함수
const getApiBaseUrl = (): string => {
  if (typeof window === 'undefined') return '';

  const host = window.location.hostname;
  const port = window.location.port;
  const protocol = window.location.protocol;


  // HTTPS에서는 항상 nginx 프록시 사용 (Mixed Content 차단 방지)
  if (protocol === 'https:') {
    return '';
  }

  // N100 내부망 (192.168.0.38) - HTTP 직접 접속 시
  if (host === '192.168.0.38') {
    return 'http://192.168.0.38:8000';
  }

  // 192.168.x.x 대역 - 내부망 HTTP 직접 접속
  if (host.startsWith('192.168.')) {
    const url = `http://${host}:8000`;
    return url;
  }

  // API_BASE_URL이 설정되어 있으면 사용
  if (API_BASE_URL) {
    return API_BASE_URL;
  }

  // nginx 프록시 사용 (상대 경로)
  return '';
};

// 회원 타입
interface Member {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  seat_id?: string | number | null;
}

// 룸 타입
interface Room {
  id: string;
  name: string;
  description?: string;
  room_type?: string;
  capacity?: number;
}

// 탭 타입
type TabType = 'seats' | 'room-assign';

const SeatManagement: React.FC = () => {
  const {
    seats,
    isLoading,
    error,
    lastRefreshTime,
    stats,
    resetAllSeats,
    loadSeats,
    selectedSeat,
    showSeatModal,
    newReason,
    setNewReason,
    handleSeatClick,
    handleStatusChange,
    closeSeatModal,
  } = useSeatManagement();

  // 탭 상태
  const [activeTab, setActiveTab] = useState<TabType>('seats');
  // 빈 좌석만 보기 필터 (회의 2026-07-04: 좌석 배정 시 빈자리 찾기 쉽게)
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);

  // 회원 배정 모달 상태
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningSeatNumber, setAssigningSeatNumber] = useState<number | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [assigning, setAssigning] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  // 룸 배치 상태
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [selectedSeatNumbers, setSelectedSeatNumbers] = useState<number[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [assigningRoom, setAssigningRoom] = useState(false);
  const [rangeStart, setRangeStart] = useState<number>(1);
  const [rangeEnd, setRangeEnd] = useState<number>(20);

  // 룸 생성 상태
  const [newRoomName, setNewRoomName] = useState<string>('');
  const [creatingRoom, setCreatingRoom] = useState(false);

  // 일괄 생성 모달 상태
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [bulkStartNumber, setBulkStartNumber] = useState(1);
  const [bulkCount, setBulkCount] = useState(50);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    created_count: number;
    skipped_count: number;
    message: string;
  } | null>(null);

  // 좌석 일괄 생성
  const handleBulkCreate = async () => {
    setBulkCreating(true);
    setBulkResult(null);
    try {
      const response = await apiClient.post('/v1/owner/seats/bulk-create', {
        start_number: bulkStartNumber,
        count: bulkCount,
        room_id: null, // 미배정
      });
      const data = response.data;
      setBulkResult({
        created_count: data.created_count,
        skipped_count: data.skipped_count,
        message: data.message,
      });
      // 좌석 목록 새로고침
      loadSeats();
    } catch (error: any) {
      console.error('좌석 일괄 생성 오류:', error);
      alert(`좌석 생성 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setBulkCreating(false);
    }
  };

  const closeBulkCreateModal = () => {
    setShowBulkCreateModal(false);
    setBulkResult(null);
  };

  // 일괄 삭제 모달 상태
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deleteStartNumber, setDeleteStartNumber] = useState(1);
  const [deleteEndNumber, setDeleteEndNumber] = useState(50);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{
    deleted_count: number;
    message: string;
  } | null>(null);

  // 좌석 일괄 삭제
  const handleBulkDelete = async () => {
    if (!window.confirm(`정말로 좌석 ${deleteStartNumber}번 ~ ${deleteEndNumber}번을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    setBulkDeleting(true);
    setDeleteResult(null);
    try {
      const response = await apiClient.post('/v1/owner/seats/bulk-delete', {
        start_number: deleteStartNumber,
        end_number: deleteEndNumber,
      });
      const data = response.data;
      setDeleteResult({
        deleted_count: data.deleted_count,
        message: data.message,
      });
      // 좌석 목록 새로고침
      loadSeats();
    } catch (error: any) {
      console.error('좌석 일괄 삭제 오류:', error);
      alert(`좌석 삭제 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const closeBulkDeleteModal = () => {
    setShowBulkDeleteModal(false);
    setDeleteResult(null);
  };

  // 룸 목록 로드
  const loadRooms = async () => {
    setLoadingRooms(true);
    try {
      const response = await apiClient.get('/v1/owner/rooms');
      setRooms(response.data || []);
    } catch (error) {
      console.error('룸 목록 로드 오류:', error);
    } finally {
      setLoadingRooms(false);
    }
  };

  // 룸 배치 탭으로 전환 시 룸 목록 로드
  useEffect(() => {
    if (activeTab === 'room-assign') {
      loadRooms();
    }
  }, [activeTab]);

  // 범위로 좌석 선택
  const handleSelectRange = () => {
    const seatNumbers: number[] = [];
    for (let i = rangeStart; i <= rangeEnd; i++) {
      // 실제 존재하는 좌석만 선택
      if (seats.some(s => parseInt(s.number) === i)) {
        seatNumbers.push(i);
      }
    }
    setSelectedSeatNumbers(seatNumbers);
  };

  // 전체 선택 (미배정 좌석만)
  const handleSelectAllUnassigned = () => {
    const unassignedSeats = seats
      .filter(s => !s.room_id)
      .map(s => parseInt(s.number));
    setSelectedSeatNumbers(unassignedSeats);
  };

  // 선택 해제
  const handleClearSelection = () => {
    setSelectedSeatNumbers([]);
  };

  // 좌석 클릭으로 선택/해제
  const toggleSeatSelection = (seatNumber: number) => {
    setSelectedSeatNumbers(prev =>
      prev.includes(seatNumber)
        ? prev.filter(n => n !== seatNumber)
        : [...prev, seatNumber]
    );
  };

  // 룸 생성
  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      alert('룸 이름을 입력해주세요.');
      return;
    }

    setCreatingRoom(true);
    try {
      await apiClient.post('/v1/owner/rooms', {
        name: newRoomName.trim(),
        description: '',
        room_type: 'reading_room',
      });
      alert(`'${newRoomName}' 룸이 생성되었습니다.`);
      setNewRoomName('');
      loadRooms(); // 룸 목록 새로고침
    } catch (error: any) {
      console.error('룸 생성 오류:', error);
      alert(`룸 생성 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setCreatingRoom(false);
    }
  };

  // 좌석 룸 배치
  const handleAssignRoom = async () => {
    if (selectedSeatNumbers.length === 0) {
      alert('배치할 좌석을 선택해주세요.');
      return;
    }

    setAssigningRoom(true);
    try {
      const response = await apiClient.post('/v1/owner/seats/bulk-assign-room', {
        seat_numbers: selectedSeatNumbers,
        room_id: selectedRoomId || null,
      });
      alert(response.data.message);
      setSelectedSeatNumbers([]);
      loadSeats();
    } catch (error: any) {
      console.error('룸 배치 오류:', error);
      alert(`룸 배치 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setAssigningRoom(false);
    }
  };

  // 회원 목록 로드 (좌석 미배정 회원만)
  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await apiClient.get('/v1/owner/members?limit=1000');
      const data = response.data;
      // 좌석 미배정 회원만 필터링
      const allMembers = (data.members || []).filter((m: any) => !m.assigned_seat && !m.assigned_seat_code);
      setMembers(allMembers);
    } catch (error) {
      console.error('회원 목록 로드 오류:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  // 좌석 배정 핸들러
  const handleAssignSeat = async () => {
    if (!selectedMemberId || !assigningSeatNumber) {
      return;
    }

    setAssigning(true);
    try {
      await apiClient.post(`/v1/owner/members/${selectedMemberId}/assign-seat`, {
        seat_id: assigningSeatNumber,
      });
      alert('좌석 배정이 완료되었습니다.');
      closeAssignModal();
      loadSeats(); // 좌석 목록 새로고침
    } catch (error: any) {
      console.error('좌석 배정 오류:', error);
      alert(`좌석 배정 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setAssigning(false);
    }
  };

  // 빈 좌석 클릭 시 회원 배정 모달 열기
  const openAssignModal = (seatNumber: number) => {
    setAssigningSeatNumber(seatNumber);
    setSelectedMemberId('');
    setMemberSearchQuery('');
    setShowAssignModal(true);
    loadMembers();
  };

  // 회원 배정 모달 닫기
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setAssigningSeatNumber(null);
    setSelectedMemberId('');
    setMemberSearchQuery('');
    setMembers([]);
  };

  // 검색어로 필터링된 회원 목록
  const filteredMembers = members.filter(
    (member) =>
      member.name.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
      (member.phone && member.phone.includes(memberSearchQuery))
  );

  // 새로고침 시간 포맷
  const formatRefreshTime = (date: Date) => {
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  /**
   * 좌석 카드 렌더링
   */
  const renderSeatCard = (seat: ManagedSeat) => {
    const getStatusIcon = (status: ManagedSeat['status']) => {
      switch (status) {
        case 'available':
          return <CheckIcon className="h-5 w-5" />;
        case 'occupied':
          return <UserIcon className="h-5 w-5" />;
        case 'maintenance':
          return <WrenchScrewdriverIcon className="h-5 w-5" />;
        case 'disabled':
          return <XMarkIcon className="h-5 w-5" />;
        default:
          return null;
      }
    };

    // 빈 좌석(available)일 때 회원 배정 모달 열기, 그 외는 기존 동작
    const handleClick = () => {
      if (seat.status === 'available') {
        openAssignModal(seat.id);
      } else {
        handleSeatClick(seat);
      }
    };

    return (
      <div
        key={seat.id}
        className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${seatManagementService.getStatusColor(seat.status)}`}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {getStatusIcon(seat.status)}
            <span className="font-semibold text-lg">좌석 {seat.number}</span>
          </div>
          <span className="text-xs px-2 py-1 rounded bg-white bg-opacity-50">
            {seat.zone}존
          </span>
        </div>

        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="text-sm font-medium">상태:</span>
            <span className="text-sm">
              {seatManagementService.getStatusText(seat.status)}
            </span>
          </div>

          {seat.user_name && (
            <div className="flex justify-between">
              <span className="text-sm font-medium">사용자:</span>
              <span className="text-sm">{seat.user_name}</span>
            </div>
          )}

          {seat.start_time && (
            <div className="flex justify-between">
              <span className="text-sm font-medium">이용 시간:</span>
              <span className="text-sm">
                {seatManagementService.getUsageTime(seat.start_time)}
              </span>
            </div>
          )}

          {seat.disabled_reason && (
            <div className="flex justify-between">
              <span className="text-sm font-medium">사유:</span>
              <span className="text-sm">{seat.disabled_reason}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">좌석 데이터 로딩 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
          <div>
            <h3 className="text-lg font-medium text-red-800">데이터 로딩 오류</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadSeats}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">좌석 관리</h1>
            <p className="text-gray-600">실시간 좌석 현황 및 관리</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="text-sm text-gray-600">
            마지막 업데이트: {formatRefreshTime(lastRefreshTime)}
          </div>

          <button
            onClick={loadSeats}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-5 w-5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('seats')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'seats'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Squares2X2Icon className="h-5 w-5 mr-2" />
            좌석 목록
          </button>
          <button
            onClick={() => setActiveTab('room-assign')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'room-assign'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <HomeModernIcon className="h-5 w-5 mr-2" />
            룸 배치
          </button>
        </nav>
      </div>

      {/* 탭 컨텐츠: 좌석 목록 */}
      {activeTab === 'seats' && (
        <>
          {/* 버튼 그룹 */}
          <div className="flex space-x-3">
            <button
              onClick={() => setShowBulkCreateModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              좌석 일괄 생성
            </button>

            <button
              onClick={() => setShowBulkDeleteModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
            >
              <TrashIcon className="h-5 w-5 mr-2" />
              좌석 일괄 삭제
            </button>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* 전체 좌석 */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-lg ${seatManagementService.getStatIconColorClass('total').background}`}
                >
                  <BuildingOfficeIcon
                    className={`h-6 w-6 ${seatManagementService.getStatIconColorClass('total').icon}`}
                  />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">전체 좌석</p>
                  <p
                    className={`text-2xl font-semibold ${seatManagementService.getStatValueColorClass('total')}`}
                  >
                    {stats.total_seats}
                  </p>
                </div>
              </div>
            </div>

            {/* 사용 가능 */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-lg ${seatManagementService.getStatIconColorClass('available').background}`}
                >
                  <CheckIcon
                    className={`h-6 w-6 ${seatManagementService.getStatIconColorClass('available').icon}`}
                  />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">사용 가능</p>
                  <p
                    className={`text-2xl font-semibold ${seatManagementService.getStatValueColorClass('available')}`}
                  >
                    {stats.available_seats}
                  </p>
                </div>
              </div>
            </div>

            {/* 사용 중 */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-lg ${seatManagementService.getStatIconColorClass('occupied').background}`}
                >
                  <UserIcon
                    className={`h-6 w-6 ${seatManagementService.getStatIconColorClass('occupied').icon}`}
                  />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">사용 중</p>
                  <p
                    className={`text-2xl font-semibold ${seatManagementService.getStatValueColorClass('occupied')}`}
                  >
                    {stats.occupied_seats}
                  </p>
                </div>
              </div>
            </div>

            {/* 점검 중 */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-lg ${seatManagementService.getStatIconColorClass('maintenance').background}`}
                >
                  <WrenchScrewdriverIcon
                    className={`h-6 w-6 ${seatManagementService.getStatIconColorClass('maintenance').icon}`}
                  />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">점검 중</p>
                  <p
                    className={`text-2xl font-semibold ${seatManagementService.getStatValueColorClass('maintenance')}`}
                  >
                    {stats.maintenance_seats}
                  </p>
                </div>
              </div>
            </div>

            {/* 이용률 */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-lg ${seatManagementService.getStatIconColorClass('occupancy').background}`}
                >
                  <PaintBrushIcon
                    className={`h-6 w-6 ${seatManagementService.getStatIconColorClass('occupancy').icon}`}
                  />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">이용률</p>
                  <p
                    className={`text-2xl font-semibold ${seatManagementService.getStatValueColorClass('occupancy')}`}
                  >
                    {stats.occupancy_rate}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 좌석 그리드 */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">좌석 현황</h2>
              {/* 회의 2026-07-04: 빈 좌석만 보기 — 배정 시 빈자리 찾기 쉽게 */}
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showOnlyAvailable}
                  onChange={(e) => setShowOnlyAvailable(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                빈 좌석만 보기
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {[...seats]
                .filter((s) => !showOnlyAvailable || s.status === 'available')
                .sort((a, b) => parseInt(a.number) - parseInt(b.number))
                .map(renderSeatCard)}
            </div>
            {showOnlyAvailable &&
              seats.filter((s) => s.status === 'available').length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6">빈 좌석이 없습니다.</p>
              )}
          </div>
        </>
      )}

      {/* 탭 컨텐츠: 룸 배치 */}
      {activeTab === 'room-assign' && (
        <div className="space-y-6">
          {/* 설명 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              좌석을 선택한 후, 배치할 룸을 선택하고 "룸에 배치" 버튼을 클릭하세요.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌석 선택 영역 */}
            <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">좌석 선택</h2>
                <span className="text-sm text-gray-600">
                  선택됨: {selectedSeatNumbers.length}개
                </span>
              </div>

              {/* 범위 선택 */}
              <div className="flex items-center space-x-3 mb-4">
                <label className="text-sm text-gray-600">범위 선택:</label>
                <input
                  type="number"
                  min={1}
                  value={rangeStart}
                  onChange={(e) => setRangeStart(parseInt(e.target.value) || 1)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <span className="text-gray-500">~</span>
                <input
                  type="number"
                  min={1}
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(parseInt(e.target.value) || 1)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <button
                  onClick={handleSelectRange}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  선택
                </button>
                <button
                  onClick={handleSelectAllUnassigned}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                >
                  미배정 전체
                </button>
                <button
                  onClick={handleClearSelection}
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                >
                  선택 해제
                </button>
              </div>

              {/* 좌석 그리드 (선택용) */}
              <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2 max-h-96 overflow-y-auto">
                {[...seats].sort((a, b) => parseInt(a.number) - parseInt(b.number)).map((seat) => {
                  const seatNum = parseInt(seat.number);
                  const isSelected = selectedSeatNumbers.includes(seatNum);
                  // room_id를 룸 이름으로 변환
                  const room = rooms.find(r => r.id === seat.room_id);
                  const roomName = room ? room.name : (seat.room_id ? seat.room_id : '미배정');

                  return (
                    <div
                      key={seat.id}
                      onClick={() => toggleSeatSelection(seatNum)}
                      className={`p-2 rounded border-2 cursor-pointer text-center transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-100'
                          : seat.room_id
                          ? 'border-green-300 bg-green-50'
                          : 'border-gray-300 bg-gray-50 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">{seat.number}</div>
                      <div className="text-xs text-gray-500 truncate">{roomName}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 룸 선택 및 배치 */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">룸 배치</h2>

              {/* 선택된 좌석 표시 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  선택된 좌석 ({selectedSeatNumbers.length}개)
                </label>
                <div className="bg-gray-50 p-3 rounded max-h-32 overflow-y-auto">
                  {selectedSeatNumbers.length === 0 ? (
                    <span className="text-gray-400 text-sm">좌석을 선택해주세요</span>
                  ) : (
                    <span className="text-sm text-gray-800">
                      {selectedSeatNumbers.sort((a, b) => a - b).join(', ')}번
                    </span>
                  )}
                </div>
              </div>

              {/* 룸 선택 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  배치할 룸
                </label>
                {loadingRooms ? (
                  <div className="flex items-center py-2">
                    <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400 mr-2" />
                    <span className="text-gray-500">룸 목록 로딩 중...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="room"
                        value=""
                        checked={selectedRoomId === ''}
                        onChange={() => setSelectedRoomId('')}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="ml-3 text-gray-700">미배정</span>
                    </label>
                    {rooms.map((room) => (
                      <label
                        key={room.id}
                        className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="radio"
                          name="room"
                          value={room.id}
                          checked={selectedRoomId === room.id}
                          onChange={() => setSelectedRoomId(room.id)}
                          className="h-4 w-4 text-blue-600"
                        />
                        <span className="ml-3 text-gray-700">{room.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* 배치 버튼 */}
              <button
                onClick={handleAssignRoom}
                disabled={selectedSeatNumbers.length === 0 || assigningRoom}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {assigningRoom ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    배치 중...
                  </>
                ) : (
                  <>
                    <HomeModernIcon className="h-5 w-5 mr-2" />
                    룸에 배치 ({selectedSeatNumbers.length}개)
                  </>
                )}
              </button>

              {/* 룸 생성 */}
              <div className="mt-6 pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-700 mb-2">새 룸 생성</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    placeholder="룸 이름 (예: 1층, A열람실)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateRoom()}
                  />
                  <button
                    onClick={handleCreateRoom}
                    disabled={creatingRoom || !newRoomName.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {creatingRoom ? '생성 중...' : '생성'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 좌석 상세 모달 */}
      {showSeatModal && selectedSeat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                좌석 {selectedSeat.seat_number} 관리
              </h3>
              <button
                onClick={closeSeatModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedSeat.current_session && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">현재 사용 정보</h4>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="font-medium">사용자:</span>{' '}
                      {selectedSeat.current_session.user.name}
                    </p>
                    <p>
                      <span className="font-medium">체크인:</span>{' '}
                      {seatManagementService.formatDateTime(
                        selectedSeat.current_session.check_in_time
                      )}
                    </p>
                    <p>
                      <span className="font-medium">이용 시간:</span>{' '}
                      {selectedSeat.current_session.current_duration_minutes ?? '-'}분
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  사유 (선택사항)
                </label>
                <input
                  type="text"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="상태 변경 사유를 입력하세요"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleStatusChange('available')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  사용 가능
                </button>
                <button
                  onClick={() => handleStatusChange('maintenance')}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  점검 모드
                </button>
                <button
                  onClick={async () => {
                    if (!selectedSeat) return;
                    try {
                      const token = localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token');
                      // seats-management에서 배정 정보 조회
                      const res = await fetch(`/v1/owner/seats-management`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!res.ok) { alert('좌석 정보 조회 실패'); return; }
                      const data = await res.json();
                      const seatData = (data.seats || []).find((s: any) => s.seat_number === selectedSeat.seat_number);
                      const assignedUser = seatData?.assigned_user;

                      if (!assignedUser?.name) {
                        alert('이 좌석에 배정된 학생이 없습니다.');
                        return;
                      }

                      if (!window.confirm(`${assignedUser.name} 학생의 ${selectedSeat.seat_number}번 좌석 배정을 해제하시겠습니까?`)) return;

                      // student_id가 있으면 release-seat, 없으면 전화번호로 학생 찾기
                      let studentId = assignedUser.id;
                      if (!studentId && assignedUser.phone) {
                        const memberRes = await fetch(`/v1/owner/members?search=${assignedUser.phone}&limit=1`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (memberRes.ok) {
                          const memberData = await memberRes.json();
                          const members = memberData.members || [];
                          if (members.length > 0) studentId = members[0].id;
                        }
                      }

                      if (!studentId) {
                        alert('학생 정보를 찾을 수 없습니다.');
                        return;
                      }

                      const releaseRes = await fetch(`/v1/owner/members/${studentId}/release-seat`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      });
                      if (releaseRes.ok) {
                        alert('좌석 배정이 해제되었습니다.');
                        closeSeatModal();
                        loadSeats();
                      } else {
                        alert('배정 해제 실패');
                      }
                    } catch (e) {
                      alert('배정 해제 중 오류 발생');
                    }
                  }}
                  className="col-span-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  배정 해제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 좌석 일괄 생성 모달 */}
      {showBulkCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <PlusCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                좌석 일괄 생성
              </h3>
              <button
                onClick={closeBulkCreateModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {bulkResult ? (
              // 결과 표시
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${bulkResult.created_count > 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <p className={`font-medium ${bulkResult.created_count > 0 ? 'text-green-800' : 'text-yellow-800'}`}>
                    {bulkResult.message}
                  </p>
                  <div className="mt-2 text-sm text-gray-600">
                    <p>생성된 좌석: {bulkResult.created_count}개</p>
                    {bulkResult.skipped_count > 0 && (
                      <p>건너뛴 좌석 (이미 존재): {bulkResult.skipped_count}개</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeBulkCreateModal}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  확인
                </button>
              </div>
            ) : (
              // 입력 폼
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  시작 번호부터 지정된 개수만큼 좌석을 자동으로 생성합니다.
                  <br />
                  룸 배정은 나중에 할 수 있습니다.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작 좌석 번호
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={bulkStartNumber}
                    onChange={(e) => setBulkStartNumber(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    생성할 좌석 개수
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={bulkCount}
                    onChange={(e) => setBulkCount(parseInt(e.target.value) || 50)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">최대 200개까지 생성 가능</p>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>생성 예정:</strong> 좌석 {bulkStartNumber}번 ~ {bulkStartNumber + bulkCount - 1}번
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={closeBulkCreateModal}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleBulkCreate}
                    disabled={bulkCreating}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    {bulkCreating ? (
                      <>
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                        생성 중...
                      </>
                    ) : (
                      '생성하기'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 좌석 일괄 삭제 모달 */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <TrashIcon className="h-6 w-6 text-red-600 mr-2" />
                좌석 일괄 삭제
              </h3>
              <button
                onClick={closeBulkDeleteModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {deleteResult ? (
              // 결과 표시
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${deleteResult.deleted_count > 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <p className={`font-medium ${deleteResult.deleted_count > 0 ? 'text-green-800' : 'text-yellow-800'}`}>
                    {deleteResult.message}
                  </p>
                  <div className="mt-2 text-sm text-gray-600">
                    <p>삭제된 좌석: {deleteResult.deleted_count}개</p>
                  </div>
                </div>
                <button
                  onClick={closeBulkDeleteModal}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  확인
                </button>
              </div>
            ) : (
              // 입력 폼
              <div className="space-y-4">
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-sm text-red-700">
                    ⚠️ 주의: 삭제된 좌석은 복구할 수 없습니다.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작 좌석 번호
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={deleteStartNumber}
                    onChange={(e) => setDeleteStartNumber(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    끝 좌석 번호
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={deleteEndNumber}
                    onChange={(e) => setDeleteEndNumber(parseInt(e.target.value) || 50)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>삭제 예정:</strong> 좌석 {deleteStartNumber}번 ~ {deleteEndNumber}번
                    <br />
                    <span className="text-gray-500">(총 {Math.max(0, deleteEndNumber - deleteStartNumber + 1)}개)</span>
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={closeBulkDeleteModal}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    {bulkDeleting ? (
                      <>
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                        삭제 중...
                      </>
                    ) : (
                      '삭제하기'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 회원 배정 모달 */}
      {showAssignModal && assigningSeatNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <UserIcon className="h-6 w-6 text-blue-600 mr-2" />
                좌석 {assigningSeatNumber}번 회원 배정
              </h3>
              <button
                onClick={closeAssignModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* 회원 검색 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  회원 검색
                </label>
                <input
                  type="text"
                  value={memberSearchQuery}
                  onChange={(e) => setMemberSearchQuery(e.target.value)}
                  placeholder="이름 또는 전화번호로 검색"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* 회원 목록 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  배정할 회원 선택 (좌석 미배정 회원만 표시)
                </label>
                {loadingMembers ? (
                  <div className="flex items-center justify-center py-4">
                    <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">회원 목록 로드 중...</span>
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    {memberSearchQuery
                      ? '검색 결과가 없습니다.'
                      : '좌석 미배정 회원이 없습니다.'}
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                    {filteredMembers.map((member) => (
                      <div
                        key={member.id}
                        className={`p-3 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${
                          selectedMemberId === member.id
                            ? 'bg-blue-100 border-l-4 border-l-blue-600'
                            : ''
                        }`}
                        onClick={() => setSelectedMemberId(member.id)}
                      >
                        <div className="font-medium text-gray-900">
                          {member.name}
                        </div>
                        {member.phone && (
                          <div className="text-sm text-gray-500">
                            {member.phone}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 선택된 회원 표시 */}
              {selectedMemberId && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>선택된 회원:</strong>{' '}
                    {filteredMembers.find((m) => m.id === selectedMemberId)?.name}
                  </p>
                </div>
              )}

              {/* 버튼 */}
              <div className="flex space-x-3">
                <button
                  onClick={closeAssignModal}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleAssignSeat}
                  disabled={!selectedMemberId || assigning}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {assigning ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                      배정 중...
                    </>
                  ) : (
                    '배정하기'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatManagement;
